/* eslint-disable @typescript-eslint/no-explicit-any */
import { type PaginationState } from '@issuemanager/api-client';
import { useLocalStorage } from '@issuemanager/apps/client/hooks';
import { useEventCallback } from '@mui/material';
import {
  type GridColumnVisibilityModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import React, {
  type Dispatch,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_PAGINATION_STATE } from '../../utils';
import { useInputRef } from '../inputs/search-box/hooks/use-input-ref';
import { RowLoadingStatus } from './types';
import { removeFromFilterByVisibility } from './utils';

export type BaseFilterModelTypeSource = 'grid' | 'quick-filter';
export interface BaseFilterModelType {
  source?: BaseFilterModelTypeSource;
  [key: string]: any;
}
export const DEFAULT_FILTER_MODEL_STATE: BaseFilterModelType = {
  source: 'grid',
};
export type DefaultFilterModelType = Record<string, any>;
export interface DataGridContextProps {
  sortModel: GridSortModel;
  setSortModel: (sortModel: GridSortModel) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  paginationModel: PaginationState;
  setPaginationModel: Dispatch<SetStateAction<PaginationState>>;
  searchTerm: string;
  onSearchTermChange: Dispatch<SetStateAction<string>>;
  searchInputRef?: React.Ref<HTMLInputElement>;
  filterModel: DefaultFilterModelType;
  setFilterModel: (
    filterModel: SetStateAction<BaseFilterModelType>,
    source?: BaseFilterModelTypeSource,
  ) => void;
  resetGridState: () => void;
  rowsLoading: RowLoadingStatus;
  setRowsAreLoading: (rows: RowLoadingStatus) => void;
}
export const DataGridContext = createContext<DataGridContextProps>({
  sortModel: [],
  columnVisibilityModel: {},
  setSortModel: () => undefined,
  onColumnVisibilityModelChange: () => undefined,
  setColumnVisibilityModel: () => undefined,
  paginationModel: DEFAULT_PAGINATION_STATE,
  setPaginationModel: () => undefined,
  searchTerm: '',
  onSearchTermChange: () => undefined,
  searchInputRef: null,
  filterModel: DEFAULT_FILTER_MODEL_STATE,
  setFilterModel: () => undefined,
  resetGridState: () => undefined,
  rowsLoading: {},
  setRowsAreLoading: () => undefined,
});

export interface DataGridProviderProps {
  children: React.ReactNode;
  storageKey: string;
}
export function DataGridProvider(props: DataGridProviderProps) {
  const { children, storageKey } = props;

  const [rowsLoading, setRowsLoading] = useState<RowLoadingStatus>({});

  const [searchTerm, setSearchTerm] = useState('');

  const { inputRef, focusInput } = useInputRef(searchTerm);

  const [paginationModel, setPaginationModel] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  const [filterModel, setFilterModel] = useState<DefaultFilterModelType>(
    DEFAULT_FILTER_MODEL_STATE,
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(
      `${storageKey}-columns-config`,
      {},
    );

  const onColumnVisibilityModelChange = useEventCallback(
    (fields: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(fields);
    },
  );

  const resetGridState = useEventCallback(() => {
    setSortModel([{ field: 'createdAt', sort: 'desc' }]);
    setFilterModel(DEFAULT_FILTER_MODEL_STATE);
    setSearchTerm('');
    setColumnVisibilityModel({});
    focusInput();
  });

  const setRowsAreLoading = useEventCallback((updateRows: RowLoadingStatus) => {
    setRowsLoading((prevRowsLoading) => ({
      ...prevRowsLoading,
      ...updateRows,
    }));
  });

  const setGridFilterModel = useEventCallback(
    (
      filterModel: SetStateAction<BaseFilterModelType>,
      source: BaseFilterModelTypeSource = 'grid',
    ) => {
      if (typeof filterModel === 'function') {
        setFilterModel((prevState) => filterModel({ ...prevState, source }));
      } else {
        setFilterModel({ ...filterModel, source });
      }
    },
  );

  useEffect(() => {
    const newFilters = removeFromFilterByVisibility(
      columnVisibilityModel,
      filterModel,
    );
    setFilterModel(newFilters);
  }, [columnVisibilityModel]);

  const contextValue = useMemo(
    () => ({
      sortModel,
      setSortModel,
      columnVisibilityModel,
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      paginationModel,
      setPaginationModel,
      searchTerm,
      onSearchTermChange: setSearchTerm,
      searchInputRef: inputRef,
      filterModel,
      setFilterModel: setGridFilterModel,
      resetGridState,
      rowsLoading,
      setRowsAreLoading,
    }),
    [
      sortModel,
      columnVisibilityModel,
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      paginationModel,
      searchTerm,
      inputRef,
      filterModel,
      setGridFilterModel,
      resetGridState,
      rowsLoading,
    ],
  );

  return (
    <DataGridContext.Provider value={contextValue}>
      {children}
    </DataGridContext.Provider>
  );
}

export function useDataGridContext() {
  return useContext(DataGridContext);
}

// TODO: Try to create a version with Zustand
